<script setup lang="ts">
import type { ISegmentedControl, ISegmentedControlVariant } from "@/types"
import { computed } from "vue"
import { Icon } from "@/components/ui"

interface SegmentedControlProps {
  modelValue: string
  label?: string
  options?: ISegmentedControl[]
  disabled?: boolean
  variant?: ISegmentedControlVariant
  noPadding?: boolean
  withBorder?: boolean
  hideLabelsBelowMd?: boolean
}

const props = withDefaults(defineProps<SegmentedControlProps>(), {
  options: () => [],
  disabled: false,
  variant: "default",
  noPadding: false,
  withBorder: false,
  hideLabelsBelowMd: false,
})

const emit = defineEmits<{
  "update:modelValue": [value: string]
}>()

const trackClass = computed(() => {
  const padding = props.noPadding ? "p-0" : "p-1"
  const layout = "inline-flex rounded-lg"

  if (!props.withBorder) {
    return [layout, padding, "bg-gray-100"]
  }

  const borderByVariant: Record<ISegmentedControlVariant, string> = {
    default: "border-gray-200",
    primary: "border-primary",
    accent: "border-nav-active",
    secondary: "gpx-segmented-track--border-secondary",
    tertiary: "gpx-segmented-track--border-tertiary",
  }

  return [layout, padding, "border bg-white", borderByVariant[props.variant]]
})

function segmentClass(isSelected: boolean, optionDisabled?: boolean) {
  const isDisabled = props.disabled || optionDisabled
  const base = "md:px-5 rounded-md border px-3 py-1 pt-1.5 text-sm font-bold font-body transition-colors"

  const disabledClass = isDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"

  if (props.variant === "default") {
    if (isSelected) {
      return [
        base,
        disabledClass,
        props.withBorder
          ? "border-transparent bg-gray-100 text-gray-700 shadow-sm"
          : "border-transparent bg-white text-gray-700 shadow-sm",
      ]
    }
    return [
      base,
      disabledClass,
      "border-transparent text-gray-700 hover:bg-gray-200",
    ]
  }

  if (isSelected) {
    const selected: Record<Exclude<ISegmentedControlVariant, "default">, string> = {
      primary: "border-primary bg-primary text-primary-btn-text shadow-none",
      accent: "border-nav-active bg-nav-active text-white shadow-none",
      secondary: "gpx-segmented-option--secondary-selected border shadow-none",
      tertiary: "gpx-segmented-option--tertiary-selected border shadow-none",
    }
    return [base, disabledClass, selected[props.variant]]
  }

  return [
    base,
    disabledClass,
    "border-transparent bg-transparent text-primary hover:bg-gray-200/80",
  ]
}

function onSelect(value: string, optionDisabled?: boolean) {
  if (props.disabled || optionDisabled)
    return
  emit("update:modelValue", value)
}
</script>

<template>
  <fieldset class="w-full border-0 p-0 m-0" data-test="segmented-control">
    <legend v-if="label" class="mb-2 ml-1 text-sm text-gray-700">
      {{ label }}
    </legend>
    <div data-test="segmented-control-track" :class="trackClass">
      <button
        v-for="option in options"
        :key="option.value"
        type="button"
        role="radio"
        data-test="segmented-control-option"
        :data-value="option.value"
        :aria-checked="modelValue === option.value"
        :disabled="disabled || option.disabled"
        :class="segmentClass(modelValue === option.value, option.disabled)"
        @click="onSelect(option.value, option.disabled)"
      >
        <span class="inline-flex items-center justify-center gap-1.5">
          <Icon v-if="option.icon" :icon="option.icon" />
          <span :class="hideLabelsBelowMd && option.icon ? 'hidden md:block' : ''">
            {{ option.label }}
          </span>
        </span>
      </button>
    </div>
  </fieldset>
</template>

<style lang="postcss">
@reference "../../../assets/styles.css";

/* Match Button base (non-hover) styles for secondary and tertiary. */
.gpx-segmented-option--secondary-selected {
  background: var(--template-secondary-colour);
  border-color: var(--template-secondary-colour);
  color: var(--template-secondary-button-text);
}

.gpx-segmented-option--tertiary-selected {
  @apply bg-white;

  border-color: var(--template-primary-colour);
  color: var(--template-primary-colour);
}

.gpx-segmented-track--border-secondary {
  border-color: var(--template-secondary-colour);
}

.gpx-segmented-track--border-tertiary {
  border-color: var(--template-primary-colour);
}
</style>
